/**
 * Container status — complete, cancel and reopen a project or list.
 *
 * The app (project header, sidebar and task context menus, the status dialog) and the server
 * (REST /v1/containers, MCP set_container_status) call these same functions.
 *
 * The rule: a project / list is never left closed with open tasks in it. Tasks of a completed or
 * canceled container drop out of Today / Upcoming (viewQueryOps.getActiveContainerIds), so such a
 * state is silent data loss from the user's point of view. Closing a container that still has open
 * tasks therefore always needs an answer for them (`remaining: "complete" | "cancel"`); without it
 * nothing is written and the result says how many tasks are open (`needsDecision`).
 *
 * Reopening reopens the container (new statusAt); the tasks and headings that were closed with it
 * keep their status, but the result names them (they carry the container's old statusAt, read before
 * it is rewritten) so the caller can offer to reopen them too, in the same call (`reopenTasks`) or
 * later (`reopenClosedWith`, which re-checks every row). The reverse holds as well - an open task
 * put into a closed container (reopened, restored, moved, added, duplicated, promoted) reopens that
 * container (`reopenContainerIfClosed`), so the rule above survives the other direction too.
 * `repairClosedContainersWithOpenTasks` heals the states that still exist from before the rule or
 * from two devices.
 *
 * Order of writes (dexie-sqlite has no rollback, a failure can stop between any two writes): the
 * open children are closed first, in chunks, and the container is closed LAST. A failure in between
 * leaves an open container with some closed children - consistent - and the retry finishes the job.
 * A container that already has the target status but still has open tasks (a half-done close from
 * before) is healed by a call with `remaining`.
 *
 * What finishing a task does besides the status (stop its timer, record a recurrence instance's
 * outcome and generate the next, advance a routine chain) runs for the tasks closed here too, and for
 * the container when it is a recurrence instance: taskCompletionHooks.
 */

import { ObjectStatus, ObjectType } from "./db-types.js"
import type { DbObject } from "./db-types.js"
import { callUndo, emitEvent, getDb, getNow } from "./runtime.js"
import { fail, ok } from "./op-types.js"
import { parseDbDate } from "./utils.js"
import { runCompletionHooksFor } from "./taskCompletionHooks.js"
import type { OpResult } from "./op-types.js"

/** What happens to a container's open tasks when it is completed or canceled. */
export type RemainingTasks = "complete" | "cancel"

/** The closable container kinds, as the API names them. */
export type ClosableKind = "project" | "collection"

/** Rows per write: a project can hold thousands of tasks. */
export const CLOSE_CHUNK_ROWS = 500

/** A section's statusAt is its tasks' plus this, so it sorts above them in the Logbook (as archiveSection does). */
const SECTION_STATUS_OFFSET_MS = 1000

/** The Project / List types: the only containers that are completed or canceled. */
export function isClosableType(type: ObjectType): boolean {
  return type === ObjectType.Project || type === ObjectType.List
}

/** The API name of a closable container type. */
export function closableKindOf(type: ObjectType): ClosableKind | undefined {
  if (type === ObjectType.Project) return "project"
  if (type === ObjectType.List) return "collection"
  return undefined
}

const isClosedStatus = (status: ObjectStatus): boolean =>
  status === ObjectStatus.Completed || status === ObjectStatus.Canceled

/**
 * Split objects into the ones a complete / cancel / reopen of a container applies to (projects and
 * lists) and the rest (areas, tasks, sections, ...), which are left alone. Used by the sidebar's
 * bulk actions, where one selection mixes kinds: every id is handled by its own type.
 */
export function partitionClosable<T extends Pick<DbObject, "type">>(
  objects: readonly T[],
): { closable: T[]; skipped: T[] } {
  const closable: T[] = []
  const skipped: T[] = []
  for (const o of objects) (isClosableType(o.type) ? closable : skipped).push(o)
  return { closable, skipped }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

/** The live, still open direct children a close acts on: tasks and sections at `root/<id>`. */
async function readOpenChildren(id: string): Promise<DbObject[]> {
  const rows = await getDb()
    .totodo.where("[parentPath+isDeleted]")
    .equals([`root/${id}`, 0])
    .toArray()
  return rows.filter(
    (c) =>
      c.status === ObjectStatus.Unfinished &&
      (c.type === ObjectType.Task || c.type === ObjectType.Section) &&
      // A recurring template is not a to-do the user sees (no view lists it) and is not closed
      // with the project: it is not counted either
      !c.isRecurringTemplate,
  )
}

const countTasks = (rows: readonly DbObject[]): number => rows.filter((c) => c.type === ObjectType.Task).length

/** A project / list this module acts on, or why not. */
async function loadClosable(id: string): Promise<DbObject | { error: string; notFound?: boolean }> {
  const obj = await getDb().totodo.get(id)
  if (!obj) return { error: `Container not found: ${id}`, notFound: true }
  if (!isClosableType(obj.type)) {
    const what =
      obj.type === ObjectType.Area
        ? "an area: areas are not completed or canceled (they have no end; their projects do)"
        : obj.type === ObjectType.Section
          ? "a section (archive it with its heading instead)"
          : "not a project or collection"
    return { error: `${id} is ${what}` }
  }
  if (obj.isDeleted === 1) return { error: `${id} is in the trash: restore it first` }
  return obj
}

// ---------------------------------------------------------------------------
// Close (complete / cancel)
// ---------------------------------------------------------------------------

export type CloseContainerResult =
  | {
      /** The container is closed (now, or it already was) and has no open tasks left. */
      done: true
      kind: ClosableKind
      alreadyClosed: boolean
      /** Open tasks that were completed / canceled with it. */
      closedTasks: number
    }
  | {
      /** Nothing was written: the container has open tasks and `remaining` was not given. */
      done: false
      needsDecision: true
      kind: ClosableKind
      openTasks: number
    }

interface Previous {
  id: string
  type: ObjectType
  status: ObjectStatus
  statusAt: string | undefined
}

const previousOf = (r: DbObject): Previous => ({
  id: r.id as string,
  type: r.type,
  status: r.status,
  statusAt: r.statusAt,
})

/** Close the open children among `ids` in chunks; each chunk re-reads its rows. Returns the rows it closed, as they were. */
async function closeChildren(
  containerId: string,
  ids: readonly string[],
  childStatus: ObjectStatus.Completed | ObjectStatus.Canceled,
  timestamp: string,
): Promise<DbObject[]> {
  const db = getDb()
  const parentPath = `root/${containerId}`
  const closed: DbObject[] = []
  for (let i = 0; i < ids.length; i += CLOSE_CHUNK_ROWS) {
    const slice = ids.slice(i, i + CLOSE_CHUNK_ROWS)
    await db.transaction("rw", db.totodo, async () => {
      const rows = await db.totodo.bulkGet(slice)
      const live = rows.filter(
        (r): r is DbObject =>
          r !== undefined &&
          r.status === ObjectStatus.Unfinished &&
          r.isDeleted === 0 &&
          r.parentPath === parentPath,
      )
      if (live.length === 0) return
      await db.totodo.bulkUpdate(live.map((r) => childUpdate(r, childStatus, timestamp)))
      closed.push(...live)
    })
  }
  return closed
}

function childUpdate(
  row: DbObject,
  childStatus: ObjectStatus,
  timestamp: string,
): { key: string; changes: { status: ObjectStatus; statusAt: string; updatedAt: string } } {
  const statusAt =
    row.type === ObjectType.Section ? String(Number(timestamp) + SECTION_STATUS_OFFSET_MS) : timestamp
  return { key: row.id as string, changes: { status: childStatus, statusAt, updatedAt: timestamp } }
}

/**
 * Complete or cancel a project / list, and with `remaining` its open tasks ("complete" or "cancel"
 * them in the same operation; the open headings of a project follow along). Without `remaining`, a
 * container with open tasks is left exactly as it is and the answer says how many (`needsDecision`):
 * the caller asks the user and calls again. Idempotent: a container that already has the status and
 * nothing open in it succeeds with `alreadyClosed`; one closed the other way is switched; one that
 * has the status but still open tasks (an interrupted close) is finished by a call with `remaining`.
 *
 * The tasks closed with it get what completeTask / cancelTask do besides the status (a running timer
 * is stopped, a recurrence instance records its outcome and generates the next one, a routine step
 * unlocks the next), and so does the container itself when it is a recurrence instance; those run
 * after the container is written.
 *
 * @param opts.skipUndo - Do not register an undo entry (the undo/redo steps themselves).
 */
export async function closeContainer(
  id: string,
  status: ObjectStatus.Completed | ObjectStatus.Canceled,
  opts?: { remaining?: RemainingTasks; skipUndo?: boolean },
): Promise<OpResult<CloseContainerResult>> {
  const db = getDb()
  try {
    const container = await loadClosable(id)
    if ("error" in container) return fail(container.error, container.notFound ? "not_found" : undefined)
    const kind = closableKindOf(container.type) as ClosableKind

    const open = await readOpenChildren(id)
    const openTasks = countTasks(open)
    const remaining = opts?.remaining
    if (openTasks > 0 && remaining === undefined) {
      return ok({ done: false, needsDecision: true, kind, openTasks })
    }
    const childStatus =
      remaining === "complete" ? ObjectStatus.Completed : remaining === "cancel" ? ObjectStatus.Canceled : undefined

    const containerAlreadyThere = container.status === status
    if (containerAlreadyThere && open.length === 0) {
      return ok({ done: true, kind, alreadyClosed: true, closedTasks: 0 })
    }

    const timestamp = getNow()

    // 1. The children first (in chunks), so a failure never leaves a closed container with open tasks
    const closed: DbObject[] =
      childStatus !== undefined && open.length > 0
        ? await closeChildren(id, open.map((c) => c.id as string), childStatus, timestamp)
        : []

    // 2. The container last. A task that arrived since the first read is caught here, in the
    //    transaction that closes the container: closed with the rest, or the close is refused.
    let late: DbObject[] = []
    let lateOpenTasks = 0
    await db.transaction("rw", db.totodo, async () => {
      const stragglers = await readOpenChildren(id)
      const stragglerTasks = countTasks(stragglers)
      if (stragglerTasks > 0 && childStatus === undefined) {
        lateOpenTasks = stragglerTasks
        return
      }
      if (stragglers.length > 0 && childStatus !== undefined) {
        await db.totodo.bulkUpdate(stragglers.map((r) => childUpdate(r, childStatus, timestamp)))
        late = stragglers
      }
      if (!containerAlreadyThere) {
        await db.totodo.update(id, { status, statusAt: timestamp, updatedAt: timestamp })
      }
    })
    if (lateOpenTasks > 0) {
      return ok({ done: false, needsDecision: true, kind, openTasks: lateOpenTasks })
    }
    const changedRows = [...closed, ...late]
    const changedChildren = changedRows.map(previousOf)
    const closedTasks = changedRows.filter((r) => r.type === ObjectType.Task).length

    // 3. What finishing a task does besides the status (timer, recurrence, routine), now that the
    //    container is closed: for the tasks closed with it, and for the container itself
    if (childStatus !== undefined) {
      await runCompletionHooksFor(
        changedRows.filter((r) => r.type === ObjectType.Task),
        childStatus === ObjectStatus.Completed ? "completed" : "canceled",
      )
    }
    if (!containerAlreadyThere) {
      await runCompletionHooksFor([container], status === ObjectStatus.Completed ? "completed" : "canceled")
    }

    if (!opts?.skipUndo) {
      const prevContainer = previousOf(container)
      const word = status === ObjectStatus.Completed ? "Completed" : "Canceled"
      callUndo(
        `${word} ${kind}`,
        async () => {
          const db = getDb()
          const ts = getNow()
          if (!containerAlreadyThere) {
            await db.totodo.update(id, {
              status: prevContainer.status,
              statusAt: prevContainer.statusAt,
              updatedAt: ts,
            })
          }
          await restorePrevious(changedChildren, ts)
        },
        async () => {
          const result = await closeContainer(id, status, { remaining, skipUndo: true })
          if (!result.ok) throw new Error(result.error)
        },
        "update",
      )
    }

    emitEvent(status === ObjectStatus.Completed ? "container_completed" : "container_canceled", {
      id,
      type: kind,
      closedTasks,
    })
    return ok({ done: true, kind, alreadyClosed: containerAlreadyThere, closedTasks })
  } catch (e) {
    return fail(`closeContainer failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

async function restorePrevious(previous: readonly Previous[], timestamp: string): Promise<void> {
  const db = getDb()
  for (let i = 0; i < previous.length; i += CLOSE_CHUNK_ROWS) {
    await db.totodo.bulkUpdate(
      previous.slice(i, i + CLOSE_CHUNK_ROWS).map((p) => ({
        key: p.id,
        changes: { status: p.status, statusAt: p.statusAt, updatedAt: timestamp },
      })),
    )
  }
}

// ---------------------------------------------------------------------------
// Reopen
// ---------------------------------------------------------------------------

/** Whether a closed `row` carries the close stamp `stampMs` (a heading's own is 1000 ms later; legacy closes wrote the same stamp). */
function closedWithStamp(row: DbObject, stampMs: number): boolean {
  if (!isClosedStatus(row.status) || row.isRecurringTemplate) return false
  const at = parseDbDate(row.statusAt)
  if (at === undefined) return false
  if (row.type === ObjectType.Task || row.type === ObjectType.Subtask) return at === stampMs
  if (row.type === ObjectType.Section) return at === stampMs || at === stampMs + SECTION_STATUS_OFFSET_MS
  return false
}

export interface ReopenContainerResult {
  kind: ClosableKind
  alreadyOpen: boolean
  /**
   * The tasks AND headings that were closed together with the container (closeContainer gives them
   * its exact statusAt; a heading's is 1000 ms later) and are still closed, read from the container's
   * old stamp before it was rewritten. Offered to the user. Tasks closed on their own, before or
   * after, carry other timestamps and are never in it. Empty when the container was open already.
   */
  closedWithIt: string[]
  /** The tasks among `closedWithIt` (what the task-only bulk endpoints take). */
  closedTaskIds: string[]
  /** The container's old close stamp (ms, as a string): pass it to reopenClosedWith to reopen `closedWithIt` later. */
  closedWithStamp?: string
  /** How many of `closedWithIt` this call reopened (`reopenTasks: true`). */
  reopened: number
}

/** The ids of the tasks and headings directly in `container` that were closed with it, by its current statusAt. */
async function findClosedWith(container: DbObject): Promise<{ rows: DbObject[]; stampMs: number | undefined }> {
  const stampMs = parseDbDate(container.statusAt)
  if (stampMs === undefined || !isClosedStatus(container.status)) return { rows: [], stampMs }
  const rows = await getDb()
    .totodo.where("[parentPath+isDeleted]")
    .equals([`root/${container.id}`, 0])
    .toArray()
  return { rows: rows.filter((r) => closedWithStamp(r, stampMs)), stampMs }
}

/** Reopen the rows of `ids` that are still closed with `stampMs`, in chunks (each re-reads its rows); returns their previous state. */
async function reopenRows(ids: readonly string[], stampMs: number, timestamp: string): Promise<Previous[]> {
  const db = getDb()
  const previous: Previous[] = []
  for (let i = 0; i < ids.length; i += CLOSE_CHUNK_ROWS) {
    const slice = ids.slice(i, i + CLOSE_CHUNK_ROWS)
    const live = (await db.totodo.bulkGet(slice)).filter(
      (r): r is DbObject => r !== undefined && r.isDeleted === 0 && closedWithStamp(r, stampMs),
    )
    if (live.length === 0) continue
    // An open task never sits in a closed container (first: the container, then the tasks)
    for (const path of new Set(live.filter((r) => r.type !== ObjectType.Section).map((r) => r.parentPath))) {
      await reopenContainerIfClosed(path)
    }
    await db.transaction("rw", db.totodo, async () => {
      await db.totodo.bulkUpdate(
        live.map((r) => ({
          key: r.id as string,
          changes: { status: ObjectStatus.Unfinished, statusAt: timestamp, updatedAt: timestamp },
        })),
      )
    })
    for (const r of live) previous.push(previousOf(r))
  }
  return previous
}

/**
 * Reopen the tasks and headings in `ids` that were closed together with a container - the
 * `closedWithIt` of reopenContainer, with its `closedWithStamp`. Every row is re-checked at write
 * time: still live, still closed, still carrying that stamp, so an offer taken late (a toast ten
 * seconds old) cannot reopen something that was changed meanwhile. Registers one undo entry.
 */
export async function reopenClosedWith(
  ids: readonly string[],
  stamp: string | number,
  opts?: { skipUndo?: boolean },
): Promise<OpResult<{ reopened: number; skipped: number }>> {
  try {
    const stampMs = parseDbDate(stamp)
    if (stampMs === undefined) return fail(`Not a close stamp: ${stamp}`)
    const previous = await reopenRows(ids, stampMs, getNow())
    if (previous.length > 0 && !opts?.skipUndo) {
      callUndo(
        `Reopened ${previous.length} to-dos`,
        async () => {
          await restorePrevious(previous, getNow())
        },
        async () => {
          const result = await reopenClosedWith(ids, stampMs, { skipUndo: true })
          if (!result.ok) throw new Error(result.error)
        },
        "batch",
      )
    }
    return ok({ reopened: previous.length, skipped: ids.length - previous.length })
  } catch (e) {
    return fail(`reopenClosedWith failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Reopen a completed or canceled project / list. The container is reopened (with a new statusAt);
 * its tasks keep the status they have (as in Things 3 and the app). The result lists what was closed
 * together with it (`closedWithIt`, computed from the container's OLD stamp before it is rewritten)
 * so the caller can offer to reopen those too. With `reopenTasks: true` exactly those are reopened
 * in the same call, after the container (an open task never sits in a closed container, and a
 * failure in between leaves the container open with some tasks closed).
 * Idempotent: an open container succeeds with `alreadyOpen` and offers nothing.
 */
export async function reopenContainer(
  id: string,
  opts?: { skipUndo?: boolean; reopenTasks?: boolean },
): Promise<OpResult<ReopenContainerResult>> {
  const db = getDb()
  try {
    const container = await loadClosable(id)
    if ("error" in container) return fail(container.error, container.notFound ? "not_found" : undefined)
    const kind = closableKindOf(container.type) as ClosableKind
    if (container.status === ObjectStatus.Unfinished) {
      return ok({ kind, alreadyOpen: true, closedWithIt: [], closedTaskIds: [], reopened: 0 })
    }

    // Before the container's statusAt changes: it is what identifies what was closed with it
    const { rows, stampMs } = await findClosedWith(container)
    const closedWithIt = rows.map((r) => r.id as string)
    const closedTaskIds = rows.filter((r) => r.type !== ObjectType.Section).map((r) => r.id as string)

    const timestamp = getNow()
    await db.totodo.update(id, { status: ObjectStatus.Unfinished, statusAt: timestamp, updatedAt: timestamp })

    let reopened: Previous[] = []
    if (opts?.reopenTasks === true && stampMs !== undefined && closedWithIt.length > 0) {
      reopened = await reopenRows(closedWithIt, stampMs, timestamp)
    }

    if (!opts?.skipUndo) {
      const prevContainer = previousOf(container)
      callUndo(
        `Reopened ${kind}`,
        async () => {
          const db = getDb()
          const ts = getNow()
          // Tasks first this time: the container goes back to closed last
          await restorePrevious(reopened, ts)
          await db.totodo.update(id, { status: prevContainer.status, statusAt: prevContainer.statusAt, updatedAt: ts })
        },
        async () => {
          const result = await reopenContainer(id, { skipUndo: true, reopenTasks: opts?.reopenTasks })
          if (!result.ok) throw new Error(result.error)
        },
        "update",
      )
    }
    emitEvent("container_reopened", { id, type: kind })
    return ok({
      kind,
      alreadyOpen: false,
      closedWithIt,
      closedTaskIds,
      ...(stampMs !== undefined ? { closedWithStamp: String(stampMs) } : {}),
      reopened: reopened.length,
    })
  } catch (e) {
    return fail(`reopenContainer failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * The other direction of the rule: an open task is about to be put into `parentPath`
 * (reopened, restored, moved, added, duplicated). When that is a closed project / list, reopen it
 * (the container alone). Call it BEFORE writing the task, so a failure in between leaves an open
 * container without the task, never an open task in a closed container.
 *
 * A path that is not `root/<container-id>` (the inbox, anyday, a task's own path), a container in
 * the trash and an open container are left alone. Returns true when it reopened one.
 */
export async function reopenContainerIfClosed(parentPath: string): Promise<boolean> {
  const segs = parentPath.split("/")
  if (segs.length !== 2 || segs[0] !== "root" || !segs[1] || segs[1].startsWith("#")) return false
  const db = getDb()
  const container = await db.totodo.get(segs[1])
  if (!container || !isClosableType(container.type) || container.isDeleted === 1) return false
  if (!isClosedStatus(container.status)) return false
  const timestamp = getNow()
  await db.totodo.update(container.id as string, {
    status: ObjectStatus.Unfinished,
    statusAt: timestamp,
    updatedAt: timestamp,
  })
  emitEvent("container_reopened", { id: container.id as string, type: closableKindOf(container.type) as string })
  return true
}

// ---------------------------------------------------------------------------
// Repair: closed containers that still have open tasks
// ---------------------------------------------------------------------------

/**
 * Closed projects / lists that still hold open tasks exist although closeContainer never makes
 * one: data from before the rule (the old sidebar close left the tasks open), two devices (one
 * closes, the other adds a task offline) and stale clients. Their tasks are hidden from Today and
 * Upcoming. This reopens each such container (the container alone, a new statusAt, no undo: a
 * background repair is not the user's action) and returns their ids.
 *
 * Run it when the data is in sync (the app's background task waits for the initial sync): a close
 * whose tasks have not arrived yet looks the same. A recurring template does not count as open.
 */
export async function repairClosedContainersWithOpenTasks(): Promise<OpResult<{ reopened: string[] }>> {
  try {
    const db = getDb()
    const candidates: DbObject[] = []
    for (const type of [ObjectType.Project, ObjectType.List]) {
      const rows = await db.totodo.where("type").equals(type).toArray()
      for (const c of rows) {
        if (c.isDeleted === 0 && isClosedStatus(c.status) && !(c.id ?? "").startsWith("#")) candidates.push(c)
      }
    }
    const reopened: string[] = []
    for (const c of candidates) {
      const open = await readOpenChildren(c.id as string)
      if (countTasks(open) === 0) continue
      const timestamp = getNow()
      await db.totodo.update(c.id as string, { status: ObjectStatus.Unfinished, statusAt: timestamp, updatedAt: timestamp })
      emitEvent("container_reopened", { id: c.id as string, type: closableKindOf(c.type) as string })
      reopened.push(c.id as string)
    }
    return ok({ reopened })
  } catch (e) {
    return fail(`repairClosedContainersWithOpenTasks failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}
